import { createSlice } from "@reduxjs/toolkit";
import update from "react-addons-update";
import { FormattedMessage } from "react-intl";
import { openSnackbar } from "store/slices/snackbar";
import axios from "utils/axios";
import { openSnackbarWithError } from "utils/errors";
import { dispatch } from "../index";
const initialState = {
  photos: {
    data: [],
    error: null,
    params: null,
    isLoading: false,
    totalCount: null,
    abortController: new AbortController(),
    replayLastRequest: false,
  },
  selectedPhotoKeys: [],
  isInQuickEntryMode: false,
  photo: {
    openModal: false,
    isLoading: false,
    isUpdating: false,
    isDeleting: false,
    data: null,
    key: null,
    index: null,
  },
  photoModal: null,
  isBatchDeleting: false,
  isBatchDeletingError: false,
  isBatchDeletingOk: false,
  isBatchAssigningTag: false,
  isBatchAssigningTagSuccess: false,
};

const slice = createSlice({
  name: "albumPhoto",
  initialState,
  reducers: {
    setIsInQuickEntryModeSuccess(state, action) {
      state.isInQuickEntryMode = action.payload;
    },

    setPaginationParams(state, action) {
      state.photos.params = action.payload;
    },
    getAlbumPhotos: (state, action) => {
      state.photos.isLoading = true;
      state.photos.replayLastRequest = false;
    },

    replayLastRequest: (state, action) => {
      state.replayLastRequest = true;
      state.displayAlert = false;
    },

    getAlbumPhotosSuccess: (state, action) => {
      state.photos.isLoading = false;
      state.photos.totalCount = action.payload.total_count;
      state.photos.data = action.payload.data;
      state.photos.error = null;
      state.photos.abortController = new AbortController();
    },
    getAlbumPhotosFailure: (state, action) => {
      state.photos.error = action.payload;
      state.photos.isLoading = false;
    },

    getPhoto: (state, action) => {
      state.photo.key = action.payload;
      state.photo.openModal = true;
      state.photo.isLoading = true;
    },

    getPhotoSuccess: (state, action) => {
      state.photo.isLoading = false;
      state.photo.data = action.payload;
      state.photo.error = null;
    },
    getPhotoFailure: (state, action) => {
      state.photo.error = action.payload;
      state.photo.isLoading = false;
    },

    closeModal: (state, action) => {
      state.photo.openModal = false;
      state.photo.data = null;
      state.photo.key = null;
    },

    // ==== update photo ====

    patchPhoto: (state, action) => {
      state.photo.isUpdating = true;
    },

    patchPhotoSuccess: (state, action) => {
      state.photo.data = update(state.photo.data, {
        $set: action.payload,
      });
      state.photo.isUpdating = false;
    },

    patchPhotoFailure: (state, action) => {
      state.photo.isUpdating = false;
    },

    // ==== add tags ====
    addTagPhoto: (state, action) => {
      state.photo.isUpdating = true;
    },

    addTagPhotoSuccess: (state, action) => {
      state.photo.isUpdating = false;
      const photoIndexKey = state.photos.data.findIndex(
        (photo) => photo.key === action.payload.key
      );
      state.photos.data = update(state.photos.data, {
        [photoIndexKey]: {
          tags: { $set: action.payload.tags },
        },
      });
      state.photo.data = update(state.photo.data, {
        $set: action.payload,
      });
    },

    addTagPhotoFailure: (state, action) => {
      state.photo.isUpdating = false;
    },

    deleteTagPhoto: (state, action) => {
      state.photo.isUpdating = true;
    },

    deleteTagPhotoSuccess: (state, action) => {
      state.photo.isUpdating = false;
      const photoIndexKey = state.photos.data.findIndex(
        (photo) => photo.key === action.payload.key
      );
      state.photos.data = update(state.photos.data, {
        [photoIndexKey]: {
          tags: { $set: action.payload.tags },
        },
      });
      state.photo.data = update(state.photo.data, {
        $set: action.payload,
      });
    },

    deleteTagPhotoFailure: (state, action) => {
      state.photo.isUpdating = false;
    },

    // ==== add Identification ====

    addIdentification: (state, action) => {
      state.photo.isUpdating = true;
    },

    addIdentificationSuccess: (state, action) => {
      const { photoKey, contestant, identification } = action.payload;
      state.photo.data = update(state.photo.data, {
        identifications: {
          $push: [identification],
        },
      });
      state.photo.isUpdating = false;
    },

    addIdentificationFailure: (state, action) => {
      state.photo.isUpdating = false;
    },

    selectIdentification(state, action) {
      const indexIdentification = state.photo.data.identifications.findIndex(
        (iden) => iden.id === action.payload.id
      );
      state.photo.data.identifications = state.photo.data.identifications.map(
        (iden) => ({
          ...iden,
          selected: (iden.selected = false),
        })
      );

      state.photo.data.identifications = update(
        state.photo.data.identifications,
        {
          [indexIdentification]: {
            selected: { $set: true },
          },
        }
      );
    },

    unSelectedIdentification(state, action) {
      const selectedIdentificationIndex =
        state.photo.data.identifications.findIndex((id) => id.selected);
      state.photo.data.identifications = update(
        state.photo.data.identifications,
        {
          [selectedIdentificationIndex]: {
            selected: { $set: false },
          },
        }
      );
    },

    // ==== ========= ====

    // ==== delete identifcation ====
    deleteIdentification(state) {
      state.photo.isUpdating = true;
    },

    deleteIdentificationSuccess(state, action) {
      const { identificationId, photoKey, identificationBib } = action.payload;
      const photoIndexKey = state.photos.data.findIndex(
        (photo) => photo.key === photoKey
      );
      state.photos.data = update(state.photos.data, {
        [photoIndexKey]: {
          identifications: {
            $apply: (identifications) => {
              return identifications.filter(
                (iden) => iden.id !== identificationId
              );
            },
          },
        },
      });
      const identificationIndex = state.photo.data.identifications.findIndex(
        (iden) => iden.id === identificationId
      );
      state.photo.data = update(state.photo.data, {
        identifications: { $splice: [[identificationIndex, 1]] },
      });
      state.photo.isUpdating = false;
    },

    deleteIdentificationFailure(state) {
      state.photo.isUpdating = false;
    },
    // ==========================

    // ==== deletePhoto ====
    deletePhoto: (state, action) => {
      state.photo.isDeleting = true;
    },

    deletePhotoSuccess: (state, action) => {
      state.photos.data = [
        ...state.photos.data.filter((photo) => photo.key !== action.payload),
      ];
      state.photos.totalCount = state.photos.totalCount - 1;
      state.photo.data = null;
      state.photo.openModal = false;
      state.photo.isDeleting = false;
    },

    deletePhotoFailure: (state, action) => {
      state.photo.isDeleting = false;
    },

    // ==== batch deleting ====

    closeBatchDeletingPhotos(state) {
      state.isBatchDeletingOk = false;
    },

    batchDeletingPhotos(state) {
      state.isBatchDeleting = true;
      state.isBatchDeletingError = false;
      state.isBatchDeletingOk = false;
    },

    batchDeletingPhotosSuccess(state, action) {
      state.isBatchDeleting = false;
      state.isBatchDeletingError = false;
      state.isBatchDeletingOk = true;
      state.selectedPhotoKeys = [];
    },
    batchDeletingPhotosFailure(state) {
      state.isBatchDeleting = false;
      state.isBatchDeletingError = true;
      state.isBatchDeletingOk = false;
    },

    // ==== batch assign tags ====
    closeAssignTagDialog(state) {
      state.isBatchAssigningTagSuccess = false;
    },

    batchAssignTagPhotos(state) {
      state.isBatchAssigningTag = true;
    },

    batchAssignTagPhotosSuccess(state, action) {
      state.isBatchAssigningTag = false;
      state.photos.data = update(state.photos.data, {
        $apply: (photos) => {
          return photos.map((photo) => {
            const photoTags = action.payload.filter(
              (pt) => pt.photo_key === photo.key
            );
            if (photoTags.length > 0) {
              photo.tags = photo.tags.concat(photoTags.map((t) => t.tag));
            }
            // If photoTag and photoTag is not already present in the photo tags
            return photo;
          });
        },
      });
      state.isBatchAssigningTagSuccess = true;
    },

    batchAssignTagPhotosFailure(state) {
      state.isBatchAssigningTag = false;
      state.isBatchAssigningTagSuccess = false;
    },

    addUploadedPhoto(state, action) {
      state.photos.data = update(state.photos.data, {
        $push: [action.payload],
      });
    },

    setBib(state, action) {
      state.photo.isUpdating = true;
    },

    setBibSuccess(state, action) {
      const { updatedIdentifications } = action.payload;
      // Find if identifications are present in the state photos and if they are present update them....
      updatedIdentifications.forEach((updatedIden) => {
        const photoIndex = state.photos.data.findIndex(
          (photo) => photo.key === updatedIden.photo_key
        );
        if (photoIndex >= 0) {
          const identificationIndex = state.photos.data[
            photoIndex
          ].identifications.findIndex((iden) => iden.id === updatedIden.id);
          state.photos.data = update(state.photos.data, {
            [photoIndex]: {
              identifications: {
                [identificationIndex]: {
                  $set: updatedIden,
                },
              },
            },
          });
        }
      });
      const index = updatedIdentifications.findIndex(
        (updatedIden) => updatedIden.photo_key === state.photo.key
      );
      const indexOfIdentificationOfCurrentPhoto =
        state.photo.data.identifications.findIndex(
          (iden) => iden.id === updatedIdentifications[index].id
        );
      // Update current photo identification
      state.photo.data = update(state.photo.data, {
        identifications: {
          [indexOfIdentificationOfCurrentPhoto]: {
            $set: updatedIdentifications[index],
          },
        },
      });
      state.photo.isUpdating = false;
    },
    selectPhoto(state, action) {
      // If already selected remove other push
      if (state.selectedPhotoKeys.includes(action.payload)) {
        state.selectedPhotoKeys = state.selectedPhotoKeys.filter(
          (key) => key !== action.payload
        );
      } else {
        state.selectedPhotoKeys.push(action.payload);
      }
    },

    selectAllPhotos(state) {
      state.selectedPhotoKeys = state.photos.data.map((photo) => photo.key);
    },

    unSelectAllPhotos(state) {
      state.selectedPhotoKeys = [];
    },
  },
});

export default slice.reducer;

async function _fetchPhotos(dispatch, albumId, params, controller) {
  try {
    dispatch(slice.actions.getAlbumPhotos());
    dispatch(slice.actions.setPaginationParams(params));
    const response = await axios.get(
      `/api/v1/photograph_albums/${albumId}/photos`,
      {
        params: params,
        signal: controller ? controller.signal : null,
      }
    );
    dispatch(slice.actions.getAlbumPhotosSuccess(response.data));
    return response.data;
  } catch (error) {
    console.log(error);
    dispatch(slice.actions.getAlbumPhotosFailure(error));
    throw error;
  }
}

export function getAlbumPhotos(
  id,
  bib,
  identificationStatus,
  awsStatus,
  tag_id,
  filename,
  orderBy,
  offset = 0,
  limit = 100
) {
  return async (dispatch, getState) => {
    const tagId = tag_id === "all" ? null : tag_id;
    try {
      const controller = getState().albumPhoto.photos.abortController;
      const aws_status = String(awsStatus);
      const params = {
        bib: bib === "" ? null : bib,
        filename: filename === "" ? null : filename,
        identification_status:
          identificationStatus === "all" ? null : identificationStatus,
        tag_id: tagId,
        aws_status: aws_status === "all" ? null : awsStatus,
        order_by: orderBy,
        offset,
        limit,
      };
      await _fetchPhotos(dispatch, id, params, controller);
    } catch (error) {
      console.log(
        `Cancelled request for ${bib} with identificationStatus=${identificationStatus} & Status=${awsStatus} & TagId=${tagId} & ${offset} & ${limit}`
      );
      dispatch(slice.actions.getAlbumPhotosFailure(error));
    }
  };
}

export function replayLastPhotosRequest() {
  return async (dispatch, getState) => {
    await _fetchPhotos(
      dispatch,
      getState().albumPhoto.photos.params,
      getState().albumPhoto.photos.abortController
    );
  };
}

export function openPhotoModal(albumId, photoKey) {
  return async () => {
    dispatch(slice.actions.getPhoto(photoKey));
    try {
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}`
      );
      dispatch(slice.actions.getPhotoSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getPhotoFailure(error));
    }
  };
}

export function getPrevPhotoModal(albumId) {
  return async (_, getState) => {
    const photoKey = getState().albumPhoto.photo.key;
    const oldParams = getState().albumPhoto.photos.params;
    const index = getState().albumPhoto.photos.data.findIndex(
      (photo) => photo.key === photoKey
    );
    let backPhotoKey = null;
    let data = getState().albumPhoto.photos.data;
    if (index < 0) {
      throw new Error("Unknown");
    }
    if (index === 0) {
      const newParams = {
        ...oldParams,
        offset: oldParams.offset - oldParams.limit,
      };
      if (newParams.offset < 0) {
        dispatch(
          openSnackbar({
            open: true,
            message: "Il n'y a pas de photos précédentes ...",
            variant: "alert",
            alert: {
              color: "warning",
            },
          })
        );
        return;
      }
      dispatch(
        openSnackbar({
          open: true,
          message: "Chargement de la nouvelle page en cours ...",
          variant: "alert",
          alert: {
            color: "info",
          },
        })
      );
      const controller = getState().albumPhoto.photos.abortController;
      data = await _fetchPhotos(dispatch, albumId, newParams, controller);
      backPhotoKey = data.data[data.data.length - 1].key;
    } else {
      backPhotoKey = data[index - 1].key;
    }
    dispatch(slice.actions.getPhoto(backPhotoKey));
    try {
      const response = await axios.get(
        `/api/v1/photograph_albums/${albumId}/photos/${backPhotoKey}`
      );
      dispatch(slice.actions.getPhotoSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getPhotoFailure(error));
    }
  };
}

export function getNextPhotoModal(albumId) {
  return async (_, getState) => {
    const photoKey = getState().albumPhoto.photo.key;
    const oldParams = getState().albumPhoto.photos.params;
    const index = getState().albumPhoto.photos.data.findIndex(
      (photo) => photo.key === photoKey
    );
    let nextPhotoKey = null;
    if (index < 0) {
      throw new Error("Unknown");
    }
    if (index === getState().albumPhoto.photos.data.length - 1) {
      if (
        oldParams.offset + getState().albumPhoto.photos.data.length <
        getState().albumPhoto.photos.totalCount
      ) {
        dispatch(
          openSnackbar({
            open: true,
            message: "Chargement de la nouvelle page en cours ...",
            variant: "alert",
            alert: {
              color: "info",
            },
          })
        );
        const controller = getState().albumPhoto.photos.abortController;
        const newParams = {
          ...oldParams,
          offset: oldParams.offset + oldParams.limit,
        };
        const newData = await _fetchPhotos(
          dispatch,
          albumId,
          newParams,
          controller
        );
        nextPhotoKey = newData.data[0].key;
      } else {
        dispatch(
          openSnackbar({
            open: true,
            message: "Il n'y a pas de photos suivantes ...",
            variant: "alert",
            alert: {
              color: "warning",
            },
          })
        );
        return;
      }
    } else {
      nextPhotoKey = getState().albumPhoto.photos.data[index + 1].key;
    }
    dispatch(slice.actions.getPhoto(nextPhotoKey));
    const newPhotoResponse = await axios.get(
      `/api/v1/photograph_albums/${albumId}/photos/${nextPhotoKey}`
    );
    dispatch(slice.actions.getPhotoSuccess(newPhotoResponse.data));
  };
}

export function addIdentification(albumId, photoKey, bib) {
  return async () => {
    dispatch(slice.actions.addIdentification());
    try {
      const response = await axios.post(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/identifications`,
        {
          bib,
        }
      );
      dispatch(
        slice.actions.addIdentificationSuccess({
          identification: response.data,
          contestant: response.data.contestant,
          photoKey: photoKey,
        })
      );
      dispatch(
        openSnackbar({
          open: true,
          message: (
            <FormattedMessage
              id={"identification.added"}
              values={{ bib: bib }}
            />
          ),
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.addIdentificationFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function selectedIdentification(identification) {
  return async () => {
    dispatch(slice.actions.selectIdentification(identification));
  };
}

export function unSelectedIdentification(identification) {
  return async () => {
    dispatch(slice.actions.unSelectedIdentification(identification));
  };
}

export function deleteIdentification(
  albumId,
  photoKey,
  identificationId,
  identifcationBib,
  contestantBib
) {
  return async () => {
    try {
      dispatch(slice.actions.deleteIdentification());
      await axios.delete(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/identifications/${identificationId}`
      );
      dispatch(
        slice.actions.deleteIdentificationSuccess({
          photoKey,
          identificationId,
          identifcationBib,
        })
      );
      dispatch(
        openSnackbar({
          open: true,
          message: (
            <FormattedMessage
              id="identification.deleted"
              values={{ bib: identifcationBib }}
            />
          ),
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteIdentificationFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function closePhotoModal() {
  return async () => {
    dispatch(slice.actions.closeModal());
  };
}

export function deletePhoto(albumId, photoKey) {
  return async () => {
    try {
      dispatch(slice.actions.deletePhoto());
      await axios.delete(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}`
      );
      dispatch(slice.actions.deletePhotoSuccess(photoKey));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="photo.deleted" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="general.error.delete" />,
          variant: "alert",
          alert: {
            color: "error",
          },
          close: false,
        })
      );
      dispatch(slice.actions.deletePhotoFailure(error));
    }
  };
}

export function batchDeletingPhotos(albumId, photoKeys) {
  return async () => {
    try {
      dispatch(slice.actions.batchDeletingPhotos());
      await axios.post(
        `/api/v1/photograph_albums/${albumId}/batch-delete-photos`,
        {
          photo_ids: photoKeys,
        }
      );
      dispatch(slice.actions.batchDeletingPhotosSuccess(photoKeys));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.batchDeletingPhotosFailure());
    }
  };
}

export function closeBatchDeletingPhotos() {
  return async () => {
    dispatch(slice.actions.closeBatchDeletingPhotos());
  };
}

export function batchAssignTagPhotos(albumId, photoKeys, tagIds) {
  return async () => {
    try {
      dispatch(slice.actions.batchAssignTagPhotos());
      const response = await axios.post(
        `/api/v1/photograph_albums/${albumId}/batch-assign-tags`,
        {
          tag_ids: tagIds,
          photo_ids: photoKeys,
        }
      );
      dispatch(slice.actions.batchAssignTagPhotosSuccess(response.data));
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.batchAssignTagPhotosFailure());
    }
  };
}

export function addUploadedPhoto(photo) {
  return async () => {
    dispatch(slice.actions.addUploadedPhoto(photo));
  };
}

export function closeAssignTagDialog() {
  return async () => {
    dispatch(slice.actions.closeAssignTagDialog());
  };
}

export function patchPhoto(albumId, photoKey, patchBody) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.patchPhoto());
      const response = await axios.put(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}`,
        {
          ...patchBody,
        }
      );
      dispatch(slice.actions.patchPhotoSuccess(response.data));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="photo.updated" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.patchPhotoFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function addTagPhoto(albumId, photoKey, tagId) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.addTagPhoto());
      const response = await axios.post(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/tags`,
        {
          tag_id: tagId,
        }
      );
      dispatch(slice.actions.addTagPhotoSuccess(response.data));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="photo.updated" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.addTagPhotoFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function deleteTagPhoto(albumId, photoKey, tagId) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.deleteTagPhoto());
      const response = await axios.delete(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/tags/${tagId}`
      );
      console.log(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/tags/${tagId}`
      );
      dispatch(slice.actions.deleteTagPhotoSuccess(response.data));
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="photo.updated" />,
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteTagPhotoFailure());
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function setBib(albumId, photoKey, identificationId, newBib) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.setBib());
      const response = await axios.post(
        `/api/v1/photograph_albums/${albumId}/photos/${photoKey}/identifications/${identificationId}/set-bib`,
        {
          bib: newBib,
        }
      );
      const updatedIdentifications = response.data;
      dispatch(
        openSnackbar({
          open: true,
          message: (
            <FormattedMessage
              id="identification.set_bib_updated"
              values={{ count: updatedIdentifications.length }}
            />
          ),
          variant: "alert",
          alert: {
            color: "success",
          },
        })
      );

      dispatch(
        slice.actions.setBibSuccess({
          updatedIdentifications,
        })
      );
    } catch (error) {
      console.log(error);
      openSnackbarWithError(error, dispatch);
    }
  };
}

export function selectPhoto(photoKey) {
  return async (dispatch) => {
    dispatch(slice.actions.selectPhoto(photoKey));
  };
}

export function selectAllPhotos() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.selectAllPhotos());
  };
}

export function unSelectAllPhotos() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.unSelectAllPhotos());
  };
}

export function setIsInQuickEntryMode(quickEntryMode) {
  return async (dispatch) => {
    dispatch(slice.actions.setIsInQuickEntryModeSuccess(quickEntryMode));
    if (quickEntryMode) {
      dispatch(
        openSnackbar({
          open: true,
          message: <FormattedMessage id="album.quick_entry_enabled" />,
          variant: "alert",
          alert: {
            color: "info",
          },
        })
      );
    }
  };
}
